import { Typography, TypographyProps } from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * Combined regex to match:
 * - Mention patterns: @[Display Name](username)
 * - Link patterns:    %[display text](url)
 */
const MENTION_OR_LINK_REGEX = /(@\[([^\]]+)\]\(([^)]+)\)|%\[([^\]]+)\]\(([^)]+)\))/g;

export interface MentionSegment {
    type: 'text' | 'mention' | 'link';
    value: string;
    /** Only present for mention segments */
    username?: string;
    /** Only present for mention segments */
    displayName?: string;
    /** Only present for link segments */
    linkText?: string;
    /** Only present for link segments */
    url?: string;
}

/**
 * Parses a string containing @[Display Name](username) and %[display text](url) patterns into segments.
 */
export function parseMentions(text: string): MentionSegment[] {
    const segments: MentionSegment[] = [];
    let lastIndex = 0;

    let match: RegExpExecArray | null;
    while ((match = MENTION_OR_LINK_REGEX.exec(text)) !== null) {
        // Add any text before this match
        if (match.index > lastIndex) {
            segments.push({
                type: 'text',
                value: text.slice(lastIndex, match.index),
            });
        }

        if (match[2] !== undefined) {
            // Mention: @[Display Name](username)
            segments.push({
                type: 'mention',
                value: match[0],
                displayName: match[2],
                username: match[3],
            });
        } else {
            // Link: %[display text](url)
            segments.push({
                type: 'link',
                value: match[0],
                linkText: match[4],
                url: match[5],
            });
        }

        lastIndex = match.index + match[0].length;
    }

    // Add any remaining text
    if (lastIndex < text.length) {
        segments.push({
            type: 'text',
            value: text.slice(lastIndex),
        });
    }

    return segments;
}

interface MentionTextProps extends Omit<TypographyProps, 'children'> {
    /** The raw text string potentially containing @[Display Name](username) mentions */
    children: string;
}

/**
 * MentionText renders a string with @[Display Name](username) patterns as
 * styled, clickable mention chips inline with the rest of the text.
 */
const MentionText: React.FC<MentionTextProps> = ({ children, ...typographyProps }) => {
    const navigate = useNavigate();
    const segments = parseMentions(children);

    if (segments.length === 1 && segments[0].type === 'text') {
        return <Typography {...typographyProps}>{children}</Typography>;
    }

    return (
        <Typography {...typographyProps}>
            {segments.map((segment, i) => {
                if (segment.type === 'text') {
                    return <span key={i}>{segment.value}</span>;
                }

                if (segment.type === 'link') {
                    return (
                        <a
                            key={i}
                            href={segment.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                color: 'inherit',
                                textDecoration: 'underline',
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            {segment.linkText}
                        </a>
                    );
                }

                return (
                    <span
                        key={i}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/profile/${segment.username}`);
                        }}
                        style={{
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.textDecoration =
                                'underline';
                        }}
                        onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.textDecoration =
                                'none';
                        }}
                    >
                        @{segment.displayName}
                    </span>
                );
            })}
        </Typography>
    );
};

export default MentionText;
